import React, { FC, KeyboardEvent, ReactNode, useState, useEffect, useRef, MouseEvent } from 'react';
import { isUndefinedNull } from 'src/utils/isDefined';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { addStorageItem, getStorage, removeStorageItem } from './functions';
import Typography from '../Typography';
import MUIIconButton from '@mui/material/IconButton';
import TextField, { TextFieldPropsType } from '../TextField';
import { colors } from 'src/themes/default';

const TextTieldBlock = styled('div')({ position: 'relative' });
const AutocompleteOptions = styled('div')({
    position: 'absolute',
    border: '1px solid #d4d4d4',
    borderBottom: 'none',
    borderTop: 'none',
    zIndex: 99,
    top: 'calc(100% - 24px)',
    left: 0,
    right: 0,
    backgroundColor: colors.background_W,

    '& > div': {
        position: 'relative',
        padding: '0px 10px',
        cursor: 'pointer',
        borderBottom: '1px solid #d4d4d4',
        lineHeight: 2,
    },
    '& > div:hover': {
        backgroundColor: colors.primary_D2,
    },
    '& > div > button': {
        right: 0,
        position: 'absolute',
        padding: '3px',
    },
});

export interface AutocompleteTextFieldPropsType extends TextFieldPropsType {
    autoComplete: string;
}

const AutocompleteTextField: FC<AutocompleteTextFieldPropsType> = (props) => {
    const inputRef = useRef(null);
    const [arrAutocomplete, setArrAutocomplete] = useState<string[]>([]);
    const [focus, setFocus] = useState<boolean>(false);
    const [currentFocus, setCurrentFocus] = useState<number>(-1);

    const { value } = props;

    const initAutocomplete = async () => {
        setArrAutocomplete((await getStorage(props.autoComplete || '')) || []);
    };

    useEffect(() => {
        initAutocomplete();
    }, []);

    useEffect((): any => {
        return () => {
            if (props.autoComplete) {
                setTimeout(() => {
                    if (!document.body.contains(inputRef.current)) {
                        addStorageItem(props.autoComplete || '', arrAutocomplete, props.value);
                    }
                });
            }
        };
    }, [props.value]);

    const inputValue = isUndefinedNull(value) ? '' : value;
    let arrFilter: string[] = [];
    const autocomplete = props.autoComplete && focus;
    if (autocomplete) {
        arrFilter = (arrAutocomplete || []).filter(
            (item) =>
                item !== inputValue && item.substr(0, inputValue.length).toUpperCase() === inputValue.toUpperCase()
        );
    }
    const hideAutocomplete =
        !autocomplete || !arrFilter.length || (arrFilter.length === 1 && arrFilter[0] === inputValue);

    const autocompleteOnFocus = () => {
        setCurrentFocus(-1);
        setFocus(true);
    };

    const autocompleteOnBlur = () => {
        setCurrentFocus(-1);
        setFocus(false);
    };

    const autocompleteOnChange = (autocompleteValue: string) => {
        if (props.onChange) {
            props.onChange(autocompleteValue, false);
        }
    };

    const autocompletOnKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (!props.autoComplete) {
            return;
        }
        if (hideAutocomplete) {
            return;
        }
        switch (e.keyCode) {
            case 40:
                e.preventDefault();
                setCurrentFocus((currentFocus + 1) % arrFilter.length);
                break;
            case 38:
                e.preventDefault();
                setCurrentFocus((currentFocus - 1 + arrFilter.length) % arrFilter.length);
                break;
            case 13:
                e.preventDefault();
                if (currentFocus > -1) {
                    autocompleteOnChange(arrFilter[currentFocus]);
                }
                break;
            default:
                setCurrentFocus(-1);
                break;
        }
    };

    const autocompletOnClick = (item: string) => {
        autocompleteOnChange(item);
    };

    const autocompleteRemoveItem = (e: MouseEvent<HTMLButtonElement>, item: string) => {
        e.preventDefault();
        e.stopPropagation();

        setArrAutocomplete(removeStorageItem(props.autoComplete || '', arrAutocomplete, item));
    };

    const autocompRenderOptions = (): ReactNode => {
        if (hideAutocomplete) {
            return null;
        }

        return (
            <AutocompleteOptions>
                {arrFilter.map((item: any, key: any): any => (
                    <Typography
                        variant="body2"
                        component="div"
                        key={String(key)}
                        onMouseDown={(): any => autocompletOnClick(item)}
                        sx={key === currentFocus ? { backgroundColor: colors.primary_D2 } : {}}
                    >
                        {item}
                        <MUIIconButton
                            size="small"
                            onMouseDown={(e: MouseEvent<HTMLButtonElement>) => {
                                autocompleteRemoveItem(e, item);
                            }}
                        >
                            <CloseIcon />
                        </MUIIconButton>
                    </Typography>
                ))}
            </AutocompleteOptions>
        );
    };

    const { autoComplete, ...otherProps } = props;

    return (
        <TextTieldBlock>
            <TextField
                {...otherProps}
                onFocus={autocompleteOnFocus}
                onBlur={autocompleteOnBlur}
                onKeyDown={autocompletOnKeyDown}
                inputRef={inputRef}
                clearValueUndefined
            />
            {autocomplete && autocompRenderOptions()}
        </TextTieldBlock>
    );
};

export default AutocompleteTextField;
